import { type ChatHistory, ChatTranscript } from "@/components/analysis/chat-transcript";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Link, Navigate, createFileRoute, useLoaderData } from "@tanstack/react-router";
import { useEffect, useState } from "react";

/**
 * `/analysis/chats/$chatId` — admin-only full transcript of a paired WhatsApp
 * chat, rendered by the shared `ChatTranscript` (messages interleaved with
 * what each analysis run created).
 *
 * E2E behaviour contract lives in `e2e/analysis-chat-history.spec.ts`.
 */

export const Route = createFileRoute("/_app/analysis/chats/$chatId")({
  component: ChatHistoryPage,
});

function ChatHistoryPage() {
  const { user } = useLoaderData({ from: "/_app" });
  const { chatId } = Route.useParams();
  const [data, setData] = useState<ChatHistory | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user.globalRole !== "admin") return;
    let alive = true;
    api
      .get<ChatHistory>(`/api/auto-sources/paired-chats/${encodeURIComponent(chatId)}/messages`)
      .then((res) => {
        if (alive) setData(res);
      })
      .catch(() => {
        if (alive) setError("Couldn't load this chat.");
      });
    return () => {
      alive = false;
    };
  }, [chatId, user.globalRole]);

  if (user.globalRole !== "admin") return <Navigate to="/" />;

  return (
    <div className="w-full space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{data?.chat.name ?? "Chat"}</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Full transcript, with what each analysis run created shown inline.
          </p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to="/agents/$slug/$tab" params={{ slug: "analyst", tab: "whatsapp" }}>
            Back
          </Link>
        </Button>
      </div>

      <ChatTranscript
        data={data}
        error={error}
        mediaUrl={(id) => `/api/auto-sources/whatsapp-media/${id}`}
      />
    </div>
  );
}
